import { useMutation } from '@tanstack/react-query';

import {
  filesApi,
  type PresignedUrlRequest,
  type UploadFileRequest,
} from '@/api/routes/common/files.api';
import { getPreparedFileName, prepareUploadFile } from '@/utils/media';
import { translateFile } from '@/i18n';

const suppressMutationSnackbars = { showUserMessage: false } as const;

/** Same flow as bodyprint-web (compress, presigned POST, direct S3 upload), images only. */
export const useFileUpload = ({ folder }: { folder?: string } = {}) => {
  const presignedUrlMutation = useMutation({
    mutationFn: (data: PresignedUrlRequest) => filesApi.presignedUrl(data),
    meta: suppressMutationSnackbars,
  });
  const uploadFileMutation = useMutation({
    mutationFn: (data: UploadFileRequest) => filesApi.uploadFile(data),
    meta: suppressMutationSnackbars,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const preparedFile = await prepareUploadFile(file);
      const presignedUrlData: PresignedUrlRequest = {
        fileName: getPreparedFileName(preparedFile),
        fileType: preparedFile.type,
        fileSize: preparedFile.size,
        ...(folder ? { folder } : {}),
      };

      const presignedUrlResponse = await presignedUrlMutation.mutateAsync(presignedUrlData);

      const uploadData: UploadFileRequest = {
        key: presignedUrlResponse.data.key,
        file: preparedFile,
        uploadUrl: presignedUrlResponse.data.uploadUrl,
        fields: presignedUrlResponse.data.fields,
        publicUrl: presignedUrlResponse.data.publicUrl,
        maxSizeBytes: presignedUrlResponse.data.maxSizeBytes,
      };

      await uploadFileMutation.mutateAsync(uploadData);

      return presignedUrlResponse.data.publicUrl;
    },
    meta: {
      defaultUserMessage: translateFile('api.hooks.common.useFileUpload', 'error'),
    },
  });

  return {
    uploadFileWithPresignedUrl: uploadMutation.mutateAsync,
    presignedUrlMutation,
    uploadFileMutation,
    uploadMutation,
    isLoading: uploadMutation.isPending,
    error: uploadMutation.error,
  };
};
